import { supabase } from './supabaseClient.ts';
import type { BlogPost, BlogListItem, BlogAdminAction } from '../types/blog.ts';

/**
 * Fetch all published blog posts for public consumption
 */
export async function getPublishedPosts(): Promise<BlogListItem[]> {
  console.log('[blogService.getPublishedPosts] Fetching published posts...');
  
  const { data, error } = await supabase
    .from('blog_posts')
    .select('slug, title, excerpt, author, tags, cover_url, status, published_at, created_at, updated_at')
    .eq('status', 'published')
    .order('published_at', { ascending: false });

  if (error) {
    console.error('[blogService.getPublishedPosts] Error:', error);
    throw new Error(`Failed to fetch blog posts: ${error.message}`);
  }

  console.log('[blogService.getPublishedPosts] Found', data?.length || 0, 'published posts');
  return data || [];
}

/**
 * Fetch a single published blog post by slug
 */
export async function getPublishedPost(slug: string): Promise<BlogPost | null> {
  console.log('[blogService.getPublishedPost] Fetching post:', slug);
  
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      console.log('[blogService.getPublishedPost] Post not found:', slug);
      return null;
    }
    console.error('[blogService.getPublishedPost] Error:', error);
    throw new Error(`Failed to fetch blog post: ${error.message}`);
  }

  console.log('[blogService.getPublishedPost] Found post:', data.title);
  return data;
}

/**
 * Admin function: Fetch all blog posts (requires authentication)
 */
export async function getAllPosts(): Promise<BlogListItem[]> {
  console.log('[blogService.getAllPosts] Fetching all posts (admin)...');
  
  // Check if user is authenticated
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !session) {
    throw new Error('Authentication required');
  }

  // Note: This will use service role permissions via RLS
  const { data, error } = await supabase
    .from('blog_posts')
    .select('slug, title, excerpt, author, tags, cover_url, status, published_at, created_at, updated_at')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[blogService.getAllPosts] Error:', error);
    throw new Error(`Failed to fetch blog posts: ${error.message}`);
  }

  console.log('[blogService.getAllPosts] Found', data?.length || 0, 'total posts');
  return data || [];
}

/**
 * Admin function: Get single post for editing (requires authentication)
 */
export async function getPost(slug: string): Promise<BlogPost | null> {
  console.log('[blogService.getPost] Fetching post for admin:', slug);
  
  // Check if user is authenticated
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !session) {
    throw new Error('Authentication required');
  }

  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      console.log('[blogService.getPost] Post not found:', slug);
      return null;
    }
    console.error('[blogService.getPost] Error:', error);
    throw new Error(`Failed to fetch blog post: ${error.message}`);
  }

  return data;
}

/**
 * Admin function: Perform actions on blog posts (publish, archive, delete)
 */
export async function performBlogAction(action: BlogAdminAction): Promise<void> {
  console.log('[blogService.performBlogAction] Performing action:', action);
  
  // Check if user is authenticated
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !session) {
    throw new Error('Authentication required');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL not configured');
  }

  // Use Edge Function for admin operations to ensure service role permissions
  const functionUrl = `${supabaseUrl}/functions/v1/blog-admin`;
  
  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(action),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[blogService.performBlogAction] Error:', response.status, errorText);
    throw new Error(`Failed to perform action: ${response.status} - ${errorText}`);
  }

  const result = await response.json();
  console.log('[blogService.performBlogAction] Action completed:', result);

  // Trigger revalidation for published posts
  if (action.type === 'publish') {
    await triggerRevalidation(`/blog/${action.slug}`);
    await triggerRevalidation('/blog');
  }
}

/**
 * Trigger ISR revalidation (React SPA doesn't have ISR, but we'll keep the interface for consistency)
 */
async function triggerRevalidation(path: string): Promise<void> {
  console.log('[blogService.triggerRevalidation] Would revalidate path:', path);
  // In a React SPA, we don't have ISR revalidation like Next.js
  // This is a no-op but keeps the interface consistent
} 